import * as React from "react";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import {
  Stack,
  Button,
  CircularProgress,
  Typography,
  Box
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import UserQuery from "../stateQueries/User";

const API_URL = process.env.REACT_APP_API_URL;

export default function StandardImageList() {
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["galleryImages"],
    queryFn: async () => await UserQuery.getAllGalleryImages()
  });

  // Get the latest gallery with up to 8 images
  const latestGallery = React.useMemo(() => {
    if (!data?.galleries || data.galleries.length === 0) return null;

    // Sort galleries by createdAt (newest first)
    const sortedGalleries = [...data.galleries].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    // Get the latest gallery and limit to 8 images
    const gallery = sortedGalleries[0];
    return {
      ...gallery,
      images: gallery.images.slice(0, 8)
    };
  }, [data]);

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isError || !latestGallery) {
    return (
      <Stack alignItems="center" justifyContent="center" py={4}>
        <Typography color="text.secondary">
          No gallery images available
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} paddingY={2}>
      {/* Gallery Title and Description */}
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "primary.main" }}
        >
          {latestGallery.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {latestGallery.description}
        </Typography>
      </Box>

      {/* Desktop Gallery - 4 columns */}
      <Stack display={{ md: "block", xs: "none" }}>
        <ImageList sx={{ width: "100%" }} cols={4} gap={10}>
          {latestGallery.images.map((imageName, index) => (
            <ImageListItem
              key={index}
              sx={{
                cursor: "pointer",
                overflow: "hidden",
                boxShadow: 3,
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: 6
                }
              }}
            >
              <img
                src={`${API_URL}/uploads/gallery/${imageName}`}
                alt={`${latestGallery.title} - ${index + 1}`}
                loading="lazy"
                style={{
                  height: 400,
                  objectFit: "cover",
                  objectPosition: "center top"
                }}
                onClick={() => {
                  window.open(
                    `${API_URL}/uploads/gallery/${imageName}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Stack>

      {/* Mobile Gallery - 2 columns */}
      <Stack display={{ md: "none", xs: "block" }}>
        <ImageList sx={{ width: "100%" }} cols={2} gap={10}>
          {latestGallery.images.map((imageName, index) => (
            <ImageListItem
              key={index}
              sx={{
                cursor: "pointer",
                overflow: "hidden",
                borderRadius: 2,
                boxShadow: 3
              }}
            >
              <img
                src={`${API_URL}/uploads/gallery/${imageName}`}
                alt={`${latestGallery.title} - ${index + 1}`}
                loading="lazy"
                style={{
                  height: 200,
                  objectFit: "cover",
                  objectPosition: "center top"
                }}
                onClick={() => {
                  window.open(
                    `${API_URL}/uploads/gallery/${imageName}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Stack>

      {/* See More Button */}
      <Stack alignItems="center" paddingY={2}>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate("/gallery")}
          sx={{
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem"
          }}
        >
          See More
        </Button>
      </Stack>
    </Stack>
  );
}
